"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  Panel,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseScientificInput } from "@/lib/tool-kit/earth-science/format";
import {
  type HeatIndexCategory,
  type HeatIndexResult,
  heatIndexCategory,
  heatIndexFrom,
} from "@/lib/tool-kit/earth-science/heat-index";
import { toCelsius } from "@/lib/tool-kit/earth-science/temperature";

/** English strings. A translated content file overrides these through `ui`. */
export const HEAT_INDEX_CALCULATOR_STRINGS = {
  temperatureLabel: "Air temperature",
  unitLabel: "Temperature unit",
  unitF: "Fahrenheit (°F)",
  unitC: "Celsius (°C)",
  humidityLabel: "Relative humidity (%)",
  humidityHint: "0–100. The NWS chart covers 40–100 %.",
  results: "Results",
  heatIndex: "Heat index (feels like)",
  inFahrenheit: "In °F",
  inCelsius: "In °C",
  risk: "NWS risk level",
  effect: "Effect on the body",
  method: "Formula used",
  methodSimple: "Simple formula (under 80 °F)",
  methodRegression: "Rothfusz regression",
  methodLow: "Rothfusz regression with the low-humidity adjustment",
  methodHigh: "Rothfusz regression with the high-humidity adjustment",
  categoryBelowRange: "Below the heat index range",
  categoryCaution: "Caution",
  categoryExtremeCaution: "Extreme caution",
  categoryDanger: "Danger",
  categoryExtremeDanger: "Extreme danger",
  effectBelowRange: "No heat index risk is defined below 80 °F.",
  effectCaution:
    "Fatigue is possible with prolonged exposure and/or physical activity.",
  effectExtremeCaution:
    "Heat stroke, heat cramps or heat exhaustion are possible with prolonged exposure and/or physical activity.",
  effectDanger:
    "Heat cramps or heat exhaustion are likely, and heat stroke is possible with prolonged exposure and/or physical activity.",
  effectExtremeDanger: "Heat stroke is highly likely.",
  workingHeading: "The working (temperatures in °F)",
  stepSimple:
    "Simple formula: 0.5 × (T + 61.0 + 1.2 × (T − 68.0) + 0.094 × RH) with T = {t} and RH = {rh} gives {simple}.",
  stepAverage:
    "Averaged with the air temperature: ({simple} + {t}) / 2 = {average}.",
  stepUnder80:
    "That is under 80 °F, so the heat index is {value} °F and the full regression is not used.",
  stepRegression:
    "At or above 80 °F the Rothfusz regression applies: HI = −42.379 + 2.04901523 T + 10.14333127 RH − 0.22475541 T RH − 0.00683783 T² − 0.05481717 RH² + 0.00122874 T² RH + 0.00085282 T RH² − 0.00000199 T² RH² = {regression}.",
  stepLow:
    "Low humidity (RH under 13 %, 80–112 °F): subtract [(13 − RH) / 4] × √([17 − |T − 95|] / 17) = {amount}.",
  stepHigh:
    "High humidity (RH over 85 %, 80–87 °F): add [(RH − 85) / 10] × [(87 − T) / 5] = {amount}.",
  stepFinal: "Heat index = {value} °F.",
  tableHeading: "Heat index chart",
  tableCorner: "Relative humidity",
  chartNote:
    "Each cell is the heat index for that air temperature and humidity, computed with the same procedure. Values assume shade and light wind. Cells marked above 140 °F (60 °C) are beyond anything the regression was fitted to.",
  beyondData: "above 140 °F",
  sunNote:
    "In full sunshine the heat index can feel up to 15 °F (8 °C) higher, and strong hot, dry winds add to the danger.",
  belowRange:
    "The heat index is only meaningful from 80 °F (27 °C) upward. Below that the value shown is simply the NWS simple formula, which stays close to the air temperature.",
  outsideChart:
    "This is outside the NWS chart's 80–110 °F and 40–100 % range, so the regression is extrapolating and the value is less reliable.",
  copyResults: "Copy results",
  copied: "Copied!",
  copyLine:
    "Heat index {value} at {temperature} and {rh} % relative humidity ({category})",
  enterValues:
    "Enter a temperature and a relative humidity to see the heat index.",
  humidityRange: "Relative humidity must be between 0 and 100 %.",
  belowZero:
    "That temperature is below absolute zero in the chosen unit. Check the number and the unit.",
} as const;

type Strings = typeof HEAT_INDEX_CALCULATOR_STRINGS;
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;
type Unit = "F" | "C";

const WHOLE = { maximumFractionDigits: 0 };
const ONE = { maximumFractionDigits: 1 };
const TWO = { maximumFractionDigits: 2 };
/** Above this the regression is far outside anything it was fitted to. */
const CHART_CAP_F = 140;
const CHART_HUMIDITIES = [40, 50, 60, 70, 80, 90, 100];
const CHART_TEMPERATURES: Record<Unit, number[]> = {
  F: [80, 85, 90, 95, 100, 105, 110],
  C: [27, 30, 33, 36, 39, 42],
};

const CATEGORY_KEYS = {
  belowRange: ["categoryBelowRange", "effectBelowRange"],
  caution: ["categoryCaution", "effectCaution"],
  extremeCaution: ["categoryExtremeCaution", "effectExtremeCaution"],
  danger: ["categoryDanger", "effectDanger"],
  extremeDanger: ["categoryExtremeDanger", "effectExtremeDanger"],
} as const satisfies Record<HeatIndexCategory, readonly (keyof Strings)[]>;

function methodLabel(result: HeatIndexResult, strings: Strings): string {
  if (result.method === "simple") {
    return strings.methodSimple;
  }
  if (result.adjustmentKind === "low") {
    return strings.methodLow;
  }
  return result.adjustmentKind === "high"
    ? strings.methodHigh
    : strings.methodRegression;
}

function workingSteps(
  result: HeatIndexResult,
  tempF: number,
  rh: number,
  strings: Strings,
  fmt: Fmt
): string[] {
  const steps = [
    t(strings.stepSimple, {
      t: fmt(tempF, TWO),
      rh: fmt(rh, TWO),
      simple: fmt(result.simple, TWO),
    }),
    t(strings.stepAverage, {
      simple: fmt(result.simple, TWO),
      t: fmt(tempF, TWO),
      average: fmt(result.averaged, TWO),
    }),
  ];
  if (result.regression === null) {
    steps.push(t(strings.stepUnder80, { value: fmt(result.fahrenheit, ONE) }));
    return steps;
  }
  steps.push(
    t(strings.stepRegression, { regression: fmt(result.regression, TWO) })
  );
  if (result.adjustmentKind !== null) {
    const key = result.adjustmentKind === "low" ? "stepLow" : "stepHigh";
    steps.push(
      t(strings[key], { amount: fmt(Math.abs(result.adjustment), TWO) })
    );
  }
  steps.push(t(strings.stepFinal, { value: fmt(result.fahrenheit, ONE) }));
  return steps;
}

/** A chart cell: the whole-degree heat index, or a "beyond the data" mark when it is absurdly high. */
function chartCell(
  fahrenheit: number,
  celsius: number,
  unit: Unit,
  fmt: Fmt,
  cap: string
): string {
  if (fahrenheit > CHART_CAP_F) {
    return cap;
  }
  return fmt(unit === "F" ? fahrenheit : celsius, WHOLE);
}

function chartRows(
  unit: Unit,
  fmt: Fmt,
  cap: string
): { head: string[]; rows: string[][] } {
  const temperatures = CHART_TEMPERATURES[unit];
  const symbol = unit === "F" ? "°F" : "°C";
  return {
    head: temperatures.map((value) => `${fmt(value)} ${symbol}`),
    rows: CHART_HUMIDITIES.map((rh) =>
      temperatures.map((value) => {
        const { fahrenheit, celsius } = heatIndexFrom(value, unit, rh);
        return chartCell(fahrenheit, celsius, unit, fmt, cap);
      })
    ),
  };
}

function Results({
  temperature,
  unit,
  rh,
  strings,
  fmt,
}: {
  temperature: number;
  unit: Unit;
  rh: number;
  strings: Strings;
  fmt: Fmt;
}) {
  const result = heatIndexFrom(temperature, unit, rh);
  const category = heatIndexCategory(result.fahrenheit);
  const [categoryKey, effectKey] = CATEGORY_KEYS[category];
  const chart = chartRows(unit, fmt, strings.beyondData);
  const symbol = unit === "F" ? "°F" : "°C";
  const shown = unit === "F" ? result.fahrenheit : result.celsius;
  const outside =
    result.inputFahrenheit > 110 || (rh < 40 && result.method === "regression");
  return (
    <>
      {result.method === "simple" ? (
        <Notice>{strings.belowRange}</Notice>
      ) : null}
      {outside ? <Notice>{strings.outsideChart}</Notice> : null}
      <StatGrid>
        <StatCard
          emphasis
          label={strings.heatIndex}
          value={`${fmt(shown, WHOLE)} ${symbol}`}
        />
        <StatCard
          label={unit === "F" ? strings.inCelsius : strings.inFahrenheit}
          value={`${fmt(unit === "F" ? result.celsius : result.fahrenheit, ONE)} ${unit === "F" ? "°C" : "°F"}`}
        />
        <StatCard label={strings.risk} value={strings[categoryKey]} />
      </StatGrid>
      <p className="text-foreground text-sm">
        <strong>{strings.effect}:</strong> {strings[effectKey]}
      </p>
      <p className={HINT}>
        {strings.method}: {methodLabel(result, strings)}. {strings.sunNote}
      </p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResults}
        small
        text={t(strings.copyLine, {
          value: `${fmt(shown, WHOLE)} ${symbol}`,
          temperature: `${fmt(temperature, ONE)} ${symbol}`,
          rh: fmt(rh, ONE),
          category: strings[categoryKey],
        })}
      />
      <Panel title={strings.workingHeading}>
        <ol className="list-decimal space-y-1 pl-5 text-foreground text-sm">
          {workingSteps(result, result.inputFahrenheit, rh, strings, fmt).map(
            (step) => (
              <li key={step}>{step}</li>
            )
          )}
        </ol>
      </Panel>
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <DataTable
        head={[strings.tableCorner, ...chart.head]}
        rows={chart.rows.map((row, index) => [
          `${fmt(CHART_HUMIDITIES[index])} %`,
          ...row,
        ])}
      />
      <p className={HINT}>{strings.chartNote}</p>
    </>
  );
}

function Outcome({
  temperature,
  humidity,
  unit,
  strings,
  fmt,
}: {
  temperature: string;
  humidity: string;
  unit: Unit;
  strings: Strings;
  fmt: Fmt;
}) {
  const typed = parseScientificInput(temperature);
  const rh = parseScientificInput(humidity);
  if (typed === null || rh === null) {
    return <Notice>{strings.enterValues}</Notice>;
  }
  if (toCelsius(typed, unit) < -273.15) {
    return <ErrorNotice>{strings.belowZero}</ErrorNotice>;
  }
  if (rh < 0 || rh > 100) {
    return <ErrorNotice>{strings.humidityRange}</ErrorNotice>;
  }
  return (
    <Results
      fmt={fmt}
      rh={rh}
      strings={strings}
      temperature={typed}
      unit={unit}
    />
  );
}

export default function HeatIndexCalculatorWidget() {
  const strings = useToolUi(HEAT_INDEX_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [temperature, setTemperature] = useState("90");
  const [unit, setUnit] = useState<Unit>("F");
  const [humidity, setHumidity] = useState("60");

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          id="hic-temperature"
          label={strings.temperatureLabel}
          numeric
          onChange={setTemperature}
          value={temperature}
        />
        <SelectField
          id="hic-unit"
          label={strings.unitLabel}
          onChange={setUnit}
          options={[
            { value: "F", label: strings.unitF },
            { value: "C", label: strings.unitC },
          ]}
          value={unit}
        />
        <TextField
          hint={strings.humidityHint}
          id="hic-humidity"
          label={strings.humidityLabel}
          numeric
          onChange={setHumidity}
          value={humidity}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome
          fmt={fmt}
          humidity={humidity}
          strings={strings}
          temperature={temperature}
          unit={unit}
        />
      </section>
    </div>
  );
}
